/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useIntl } from 'react-intl';
import { CSSProperties, useMemo, useState, useEffect } from 'react';
import {
  Checkbox,
  Input,
  Popover,
  Table,
  Typography,
  Collapse,
  Empty,
  Tag,
  message,
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import IconButton from '~components/_common/IconButton/IconButton';
import { useRegistrationLabelGroups } from '~src/hooks/api-hooks/useRegistrationLabelGroups';
import {
  Label,
  RegistrationLabel,
  restoreGroup,
} from '~src/services/api/labels';
import { TableProps } from 'antd/lib/table';
import { DB_STATE } from '~src/types/enums';
import { tabStyleFn } from './tabs.util';
import { CreateEditLabelGroupModal } from '~components/popups-modals/CreateLabelGroup.modal';
import { CreateEditLabelModal } from '~components/popups-modals/CreateLabel.modal';
import { DeleteLabelModal } from '~components/popups-modals/DeleteLabel.modal';
import { RestoreLabelModal } from '~components/popups-modals/RestoreLabel.modal';
import { DeleteLabelGroup } from '~components/popups-modals/DeleteLabelGroup.modal';
import { IconPopup } from '~components/popups-modals/DetailsLayouts/components';
import { useProjectId } from '~src/hooks/useProjectId';
import { useMutation } from 'react-query';

const { Panel } = Collapse;
const { Text } = Typography;

export const LabelsTab = () => {
  const intl = useIntl();
  const style = useStyles(styleFn);
  const tabStyles = useStyles(tabStyleFn);

  const projectId = useProjectId();
  const [searchValue, setSearchValue] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [activeGroupIds, setActiveGroupIds] = useState<string[] | string>([]);

  const registrationLabelGroups = useRegistrationLabelGroups(showDeleted);

  const [
    createEditGroupModalVisible,
    setCreateEditGroupModalVisible,
  ] = useState(false);
  const [deleteLabelModal, setDeleteLabelModal] = useState(false);
  const [restoreLabelModal, setRestoreLabelModal] = useState(false);
  const [deleteGroupModal, setDeleteGroupModal] = useState(false);
  const [
    createEditLabelModalVisible,
    setCreateEditLabelModalVisible,
  ] = useState(false);

  const [selectedGroup, setSelectedGroup] = useState<RegistrationLabel | null>(
    null
  );
  const [selectedLabel, setSelectedLabel] = useState<Label | null>(null);

  // --- Open all groups by default when data is loaded ---
  useEffect(() => {
    if (
      registrationLabelGroups.data &&
      registrationLabelGroups.data.length > 0
    ) {
      const allIds = registrationLabelGroups.data.map((g) => String(g.id));
      setActiveGroupIds(allIds);
    }
  }, [registrationLabelGroups.data]);

  const handleToggleAll = () => {
    if (Array.isArray(activeGroupIds) && activeGroupIds.length > 0) {
      setActiveGroupIds([]);
    } else {
      const allIds = filteredRegistrationLabelGroups.map((g) => String(g.id));
      setActiveGroupIds(allIds);
    }
  };

  const isGroupDeleted = (group: RegistrationLabel) =>
    Number(group.state) === DB_STATE.REMOVED;

  const getColorRow = (record) =>
    Number(record.state) === DB_STATE.REMOVED ? '#ffd4d9' : '';

  const [doReactivateGroupMutation, reactivateMutationState] = useMutation(
    (group: RegistrationLabel) => {
      if (!projectId) throw new Error('No projectId');
      return restoreGroup({
        projectId: Number(projectId),
        groupId: group.id,
        action: 'reactivate',
      });
    },
    {
      onSuccess: () => {
        message.success(
          intl.formatMessage({
            id: 'labels-reactivate-group-success',
            defaultMessage: 'Group reactivated',
          })
        );
        registrationLabelGroups.refetch?.();
      },
      onError: () => {
        message.error(
          intl.formatMessage({
            id: 'labels-reactivate-group-failed',
            defaultMessage: 'Failed to reactivate group',
          })
        );
      },
    }
  );

  const reactivateGroupIsLoading =
    typeof reactivateMutationState === 'boolean'
      ? reactivateMutationState
      : !!(reactivateMutationState as any)?.isLoading;

  // --- Search & Filter Logic ---
  const filteredRegistrationLabelGroups = useMemo(() => {
    const data = registrationLabelGroups.data ?? [];
    if (!searchValue && showDeleted) return data;

    return (
      data
        .map((g) => {
          const searchLower = searchValue.toLowerCase();
          const groupMatches = g.name.toLowerCase().includes(searchLower);

          // 1. Filter labels based on deletion state
          let visibleLabels = g.labels.filter(
            (l) => showDeleted || Number(l.state) !== DB_STATE.REMOVED
          );

          // 2. Filter labels by search string
          if (searchValue) {
            visibleLabels = visibleLabels.filter((l) =>
              l.title.toLowerCase().includes(searchLower)
            );
          }

          return { ...g, labels: visibleLabels, matchesSearch: groupMatches };
        })
        .filter((g) => {
          if (searchValue) {
            return g.matchesSearch || g.labels.length > 0;
          }
          return true;
        })
        // Sorting Logic - Always sort by Order
        .sort((a, b) => (a.order > b.order ? 1 : -1))
    );
  }, [registrationLabelGroups.data, searchValue, showDeleted]);

  // --- Table Columns with Wrapping ---
  const columns: (groupId: number) => TableProps<Label>['columns'] = (
    groupId
  ) => [
    {
      key: 'labelStructure',
      title: '',
      dataIndex: 'remove',
      width: 40,
      align: 'center',
      onCell: (record) => ({
        style: {
          background: getColorRow(record),
          verticalAlign: 'middle',
          cursor: 'pointer',
          padding: '4px',
        },
      }),
      render: (title, node) => (
        <div
          onClick={() => {
            setSelectedLabel(node);
            setSelectedGroup(
              registrationLabelGroups.data!.find((g) => g.id === groupId)! ??
                null
            );
            Number(node?.state) === DB_STATE.REMOVED
              ? setRestoreLabelModal(true)
              : setDeleteLabelModal(true);
          }}
          css={style.actionContainer}
          style={{ justifyContent: 'center' }}>
          <IconButton
            icon={
              Number(node?.state) === DB_STATE.REMOVED
                ? ['far', 'rotate-left']
                : ['far', 'circle-xmark']
            }
            customStyle={[
              tabStyles.folderIcon,
              Number(node?.state) === DB_STATE.REMOVED
                ? style.blackIcon
                : style.redIcon,
            ]}
            containerStyle={style.iconRemove}
            backgroundOff
            tooltip={
              Number(node?.state) === DB_STATE.REMOVED
                ? intl.formatMessage({ id: 'labels-restore' })
                : intl.formatMessage({ id: 'labels-delete' })
            }
          />
        </div>
      ),
    },
    {
      key: 'labelStructureTitle',
      title: intl.formatMessage({ id: 'label' }),
      dataIndex: 'title',
      onCell: (record) => ({
        style: {
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          background: getColorRow(record),
          verticalAlign: 'middle',
        },
      }),
      render: (title) => <Text style={{ width: '100%' }}>{title}</Text>,
    },
    {
      key: 'labelStructureAction',
      title: intl.formatMessage({ id: 'action' }),
      dataIndex: 'action',
      width: 80,
      align: 'center',
      onCell: (record) => ({
        style: {
          whiteSpace: 'nowrap',
          background: getColorRow(record),
          verticalAlign: 'middle',
        },
      }),
      render: (title, record) => (
        <div
          onClick={() => {
            setSelectedLabel(record);
            setSelectedGroup(
              registrationLabelGroups.data!.find((g) => g.id === groupId)!
            );
            setCreateEditLabelModalVisible(true);
          }}
          css={[style.actionContainer, style.editColor]}
          style={{ cursor: 'pointer', justifyContent: 'center' }}>
          <IconButton
            icon={['far', 'pen']}
            customStyle={style.editIcon}
            containerStyle={tabStyles.folderIconContainer}
            backgroundOff
          />
          <span style={{ marginLeft: 4 }}>
            {intl.formatMessage({ id: 'edit' })}
          </span>
        </div>
      ),
    },
  ];

  const getGroupActionsPopup = (group: RegistrationLabel) => {
    const deleted = isGroupDeleted(group);

    return (
      <div style={{ cursor: 'pointer', margin: '-12px 0' }}>
        {/* Add label */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            opacity: deleted ? 0.45 : 1,
            pointerEvents: deleted ? 'none' : 'auto',
          }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: 30,
              height: 30,
            }}>
            <IconButton
              icon={['far', 'square-plus']}
              containerStyle={style.iconRemove}
              backgroundOff
            />
          </div>

          <p
            onClick={() => {
              setSelectedGroup(group);
              setSelectedLabel(null);
              setCreateEditLabelModalVisible(true);
            }}
            style={{ padding: 10, margin: 0 }}>
            {intl.formatMessage({ id: 'labels-create-label' })}
          </p>
        </div>

        {/* Edit group */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            opacity: deleted ? 0.45 : 1,
            pointerEvents: deleted ? 'none' : 'auto',
          }}>
          <IconButton
            icon={['far', 'pen']}
            containerStyle={style.iconRemove}
            backgroundOff
          />
          <p
            onClick={() => {
              setSelectedGroup(group);
              setSelectedLabel(null);
              setCreateEditGroupModalVisible(true);
            }}
            style={{ padding: 10, margin: 0 }}>
            {intl.formatMessage({
              id: 'labels-edit-group-title',
              defaultMessage: 'Edit group',
            })}
          </p>
        </div>

        {/* Delete OR Reactivate */}
        {deleted ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}>
            <IconButton
              icon={['far', 'rotate-left']}
              containerStyle={style.iconRemove}
              backgroundOff
            />
            <p
              onClick={() => {
                if (reactivateGroupIsLoading) return;
                doReactivateGroupMutation(group);
              }}
              style={{
                padding: 10,
                margin: 0,
                opacity: reactivateGroupIsLoading ? 0.6 : 1,
                cursor: reactivateGroupIsLoading ? 'not-allowed' : 'pointer',
              }}>
              {intl.formatMessage({
                id: 'labels-reactivate-group-title',
                defaultMessage: 'Reactivate group',
              })}
            </p>
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}>
            <IconButton
              icon={['far', 'trash']}
              containerStyle={style.iconRemove}
              backgroundOff
            />
            <p
              onClick={() => {
                setSelectedGroup(group);
                setSelectedLabel(null);
                setDeleteGroupModal(true);
              }}
              style={{ padding: 10, margin: 0 }}>
              {intl.formatMessage({ id: 'delete' })}
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
        }}>
        <h3 css={style.heading}>
          {intl.formatMessage({ id: 'labels-settings' })}
        </h3>
        <IconPopup
          headerId={'label-settings-help-header'}
          messageId={'label-settings-help-body'}
          customStyle={{ fontSize: '130%' }}
        />
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '10px',
        }}>
        <div
          css={[style.containerSearch, { marginTop: '10px' }]}
          key="searchBarContainer">
          <Input
            prefix={
              <FontAwesomeIcon
                icon={['far', 'search']}
                style={style.icons as CSSProperties}
              />
            }
            css={style.inputField}
            placeholder={intl.formatMessage({ id: 'search' })}
            allowClear
            onChange={(e) => setSearchValue(e.target.value)}
          />
        </div>

        <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
          {/* ADD GROUP */}
          <div
            css={style.buttonDiv}
            onClick={() => {
              setCreateEditGroupModalVisible(true);
            }}>
            <FontAwesomeIcon
              icon={['far', 'rectangle-history-circle-plus']}
              style={style.icons as CSSProperties}
            />
            <p css={{ marginBottom: 0, marginLeft: 2 }}>
              {intl.formatMessage({ id: 'labels-create-group' })}
            </p>
          </div>

          {/* SHOW ALL / CLOSE ALL */}
          <div css={style.buttonDiv} onClick={handleToggleAll}>
            <FontAwesomeIcon
              icon={
                Array.isArray(activeGroupIds) && activeGroupIds.length > 0
                  ? ['far', 'layer-minus']
                  : ['far', 'layer-plus']
              }
              style={style.icons as CSSProperties}
            />
            <p css={{ marginBottom: 0, marginLeft: 2 }}>
              {Array.isArray(activeGroupIds) && activeGroupIds.length > 0
                ? intl.formatMessage({
                    id: 'close-all',
                  })
                : intl.formatMessage({
                    id: 'show-all',
                  })}
            </p>
          </div>
        </div>
      </div>

      {/* --- COLLAPSE / TABLE AREA --- */}
      <div
        css={[
          style.rowContainer,
          {
            height: '100%',
            maxHeight: 'calc(80vh - 200px)',
            overflowY: 'auto',
            paddingRight: '5px',
          },
        ]}>
        {filteredRegistrationLabelGroups &&
        filteredRegistrationLabelGroups.length > 0 ? (
          <Collapse
            activeKey={activeGroupIds}
            onChange={(keys) => setActiveGroupIds(keys)}
            expandIconPosition="left"
            bordered={false}
            css={style.collapseContainer}>
            {filteredRegistrationLabelGroups.map((group) => {
              const deleted = isGroupDeleted(group);

              return (
                <Panel
                  key={String(group.id)}
                  css={[
                    style.collapsePanel,
                    deleted && style.collapsePanelDeleted,
                  ]}
                  header={
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                      }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          flex: 1,
                          paddingRight: 10,
                        }}>
                        <Text
                          style={{
                            fontSize: '14px',
                            fontWeight: 'bold',
                            wordBreak: 'break-word',
                            whiteSpace: 'normal',
                            lineHeight: '1.2',
                            opacity: deleted ? 0.75 : 1,
                          }}>
                          {group.name}
                        </Text>

                        {deleted && (
                          <Tag style={{ margin: 0 }}>
                            {intl.formatMessage({
                              id: 'deleted',
                              defaultMessage: 'Deleted',
                            })}
                          </Tag>
                        )}
                      </div>

                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          flexShrink: 0,
                        }}
                        onClick={(e) => e.stopPropagation()}>
                        <div
                          css={style.buttonDiv}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (deleted) return;
                            setSelectedGroup(group);
                            setCreateEditLabelModalVisible(true);
                          }}
                          style={{
                            opacity: deleted ? 0.45 : 1,
                            pointerEvents: deleted ? 'none' : 'auto',
                          }}>
                          <IconButton
                            icon={['far', 'square-plus']}
                            containerStyle={style.iconRemove}
                            backgroundOff
                          />
                          <p css={{ marginBottom: 0, marginLeft: 2 }}>
                            {intl.formatMessage({ id: 'labels-create-label' })}
                          </p>
                        </div>

                        <Popover
                          content={getGroupActionsPopup(group)}
                          trigger={'click'}
                          placement={'right'}>
                          <div
                            css={style.buttonDiv}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}>
                            <IconButton
                              customStyle={style.iconRotate}
                              iconStyle="far"
                              icon="ellipsis"
                              backgroundOff
                            />
                          </div>
                        </Popover>
                      </div>
                    </div>
                  }>
                  <div
                    style={{
                      background: deleted ? '#fff1f0' : getColorRow(group),
                      opacity: deleted ? 0.85 : 1,
                    }}>
                    <Table<Label>
                      css={style.customTable}
                      showSorterTooltip={false}
                      bordered
                      pagination={false}
                      dataSource={group.labels}
                      columns={columns(group.id)}
                      rowKey="id"
                    />
                  </div>
                </Panel>
              );
            })}
          </Collapse>
        ) : (
          <Empty
            description={intl.formatMessage({ id: 'no-data' })}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ marginTop: '20px' }}
          />
        )}
      </div>

      {/* show deleted */}
      <div
        style={{
          marginTop: '8px',
          display: 'flex',
          justifyContent: 'flex-end',
        }}>
        <Checkbox
          checked={showDeleted}
          onChange={(e) => setShowDeleted(e.target.checked)}>
          {intl.formatMessage({ id: 'show-deleted' })}
        </Checkbox>
      </div>

      {/* Modals */}
      {
        <CreateEditLabelGroupModal
          visible={createEditGroupModalVisible}
          group={selectedGroup}
          groupsAmount={registrationLabelGroups.data?.length ?? 1}
          onCancel={() => {
            setCreateEditGroupModalVisible(false);
            setSelectedLabel(null);
            setSelectedGroup(null);
          }}
        />
      }
      {
        <CreateEditLabelModal
          visible={createEditLabelModalVisible}
          group={selectedGroup}
          label={selectedLabel}
          onCancel={() => {
            setCreateEditLabelModalVisible(false);
            setSelectedLabel(null);
            setSelectedGroup(null);
          }}
        />
      }
      {
        <DeleteLabelModal
          visible={deleteLabelModal}
          group={selectedGroup}
          label={selectedLabel}
          onCancel={() => {
            setDeleteLabelModal(false);
            setSelectedLabel(null);
            setSelectedGroup(null);
          }}
        />
      }
      {
        <RestoreLabelModal
          visible={restoreLabelModal}
          group={selectedGroup}
          label={selectedLabel}
          onCancel={() => {
            setRestoreLabelModal(false);
            setSelectedLabel(null);
            setSelectedGroup(null);
          }}
        />
      }
      {
        <DeleteLabelGroup
          visible={deleteGroupModal}
          group={selectedGroup}
          onCancel={() => {
            setDeleteGroupModal(false);
            setSelectedLabel(null);
            setSelectedGroup(null);
          }}
        />
      }
    </div>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  whiteHeader: {
    color: 'white',
  },
  collapseContainer: {
    background: 'transparent',
    border: 'none',
  },
  collapsePanel: {
    marginBottom: 10,
    background: '#fff',
    borderRadius: 4,
    border: `1px solid ${theme.colors.borderLight || '#d9d9d9'}`,
    overflow: 'hidden',
    // Header Styling
    '& .ant-collapse-header': {
      backgroundColor: '#fafafa',
      alignItems: 'center !important',
      borderBottom: '1px solid transparent',
      transition: 'border-bottom 0.3s',
    },
    '&.ant-collapse-item-active .ant-collapse-header': {
      borderBottom: `1px solid ${theme.colors.borderLight || '#d9d9d9'}`,
    },
    '& .ant-collapse-content': {
      borderTop: 'none',
    },
    // Remove padding from the content body so table is full width
    '& .ant-collapse-content-box': {
      padding: '0 !important',
    },
  },

  collapsePanelDeleted: {
    border: '1px solid #ff9aa5',
    '& .ant-collapse-header': {
      backgroundColor: '#fff1f0',
    },
  },

  customTable: {
    overflowY: 'hidden',
    scrollbarWidth: 'none',
    '& .ant-table-cell': {
      padding: '8px 8px !important',
    },
    '& .ant-table': {
      border: 'none',
    },
    '& .ant-table-container': {
      border: 'none',
    },
  },
  buttonDiv: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    type: 'button',
    cursor: 'pointer',
    marginLeft: 4,
    padding: '2px 10px',
    ':hover': {
      backgroundColor: theme.colors.gray,
      borderRadius: 9,
    },
  },
  iconPlus: {
    fontSize: '20px',
    color: theme.colors.primary,
    transition: '0.3s',
  },
  iconRotate: {
    transform: 'rotate(90deg)',
  },
  redIcon: {
    color: theme.colors.red,
  },
  blackIcon: {
    color: 'black',
  },
  editColor: {
    color: theme.colors.darkGray,
    ':hover': {
      color: theme.colors.primary,
    },
  },
  editIcon: {
    fontSize: 18,
    marginTop: -7,
  },
  actionContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  iconRemove: {
    // styles for icon container
  },
  heading: {
    margin: 0,
    fontSize: '16px',
    fontWeight: 600,
  },
  containerSearch: {
    flex: 1,
    marginRight: 15,
  },
  inputField: {
    width: '100%',
  },
  icons: {
    fontSize: 14,
    color: theme.colors.darkGray,
    marginRight: 5,
  },
  rowContainer: {
    // any general container styles
  },
});
